import {createContext, type Dispatch, type PropsWithChildren, type SetStateAction, useContext, useState} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import type {DungeonFormInput, DungeonFormOutput} from "@/dungeons/admin/form/DungeonFormSchema.ts";
import type {DungeonViewport} from "./DungeonViewport.ts";

type DungeonLevel = DungeonFormOutput["levels"][number];

type DungeonEditorSession = {
    levels: DungeonLevel[];
    activeLevel?: DungeonLevel;
    activeLevelIndex: number;
    selectLevel: (levelId: string) => void;
    viewport?: DungeonViewport;
    setViewport: Dispatch<SetStateAction<DungeonViewport | undefined>>;
    snappingEnabled: boolean;
    setSnappingEnabled: (enabled: boolean) => void;
    lightingPreview: boolean;
    setLightingPreview: (enabled: boolean) => void;
};

const DungeonEditorSessionContext = createContext<DungeonEditorSession | undefined>(undefined);

export function DungeonEditorSessionProvider({children}: PropsWithChildren) {
    const form = useFormContext<DungeonFormInput, unknown, DungeonFormOutput>();
    const levels = (useWatch({control: form.control, name: "levels"}) ?? []) as DungeonLevel[];
    const [selectedLevelId, setSelectedLevelId] = useState<string>();
    const activeLevelIndex = Math.max(0, levels.findIndex(level => level.id === selectedLevelId));
    const activeLevel = levels[activeLevelIndex];
    const [viewport, setViewport] = useState<DungeonViewport>();
    const [snappingEnabled, setSnappingEnabled] = useState(true);
    const [lightingPreview, setLightingPreview] = useState(false);

    return <DungeonEditorSessionContext.Provider value={{
        levels,
        activeLevel,
        activeLevelIndex,
        selectLevel: setSelectedLevelId,
        viewport,
        setViewport,
        snappingEnabled,
        setSnappingEnabled,
        lightingPreview,
        setLightingPreview,
    }}>{children}</DungeonEditorSessionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useDungeonEditorSession = () => {
    const session = useContext(DungeonEditorSessionContext);
    if (!session) throw new Error("useDungeonEditorSession must be used inside DungeonEditorSessionProvider");
    return session;
};
